import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ISessionAttendance, ISessionAttendanceFE, IUserSessionAttendanceFE, SessionAttendanceState } from 'src/app/shared/models/session-attendance/session-attendance.model';
import { FormControl, FormGroup, FormsModule } from '@angular/forms';
import { UserStoreService } from 'src/app/shared/services/store/user-store.service';
import { SelectedOrgService } from 'src/app/shared/services/selected-org.service';
import { ModalService } from 'src/app/shared/services/modal.service';
import { IUser } from 'src/app/shared/models/user/user.model';
import { RoleType } from 'src/app/shared/enums/user/role-types.enum';
import { AdminCustomerCardModalComponent, AdminCustomerCardTab } from 'src/app/pages/admin/org-admin/components/modals/customer-card-modal/admin-customer-card-modal.component';
import { LecturerCustomerCardModalComponent, LecturerCustomerCardTab } from 'src/app/pages/admin/org-admin/components/modals/lecturer-customer-card-modal/lecturer-customer-card-modal.component';
import { UserNameOrEmailPipe } from 'src/app/shared/pipes/user-name-or-email.pipe';
import { RecordPaymentModalComponent } from 'src/app/pages/admin/org-admin/payments-tabs/payments/record-payment-modal/record-payment-modal.component';
import { LocalizedDatePipe } from 'src/app/shared/pipes/localized-date.pipe';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { SelectButtonModule, SelectButtonOptionClickEvent } from 'primeng/selectbutton';
import { SelectItem } from 'primeng/api';
import { ReservationState } from 'src/app/shared/enums/reservation/reservation-states.enum';
import { ISelectButtonColoredItem } from '../../select-button-colored/select-button-colored.component';
import { Color } from 'src/app/shared/enums/utils/colors';
import { Subscription } from 'rxjs';
import { SessionAttendanceStatusBarComponent } from '../session-attendance-status-bar/session-attendance-status-bar.component';
import { ReservationStatusComponent } from '../reservation-status/reservation-status.component';
import { SessionGroupCoupleRole } from 'src/app/shared/enums/session-group/session-group-couple-roles.enum';
import { TranslateModule } from '@ngx-translate/core';

export interface IUserSessionAttendanceFG {
  state: FormControl<SessionAttendanceState | null>;
  excused: FormControl<boolean>;
  replaced: FormControl<boolean>;
  sessionId: FormControl<number>;
  userId: FormControl<number>;
}

@Component({
  selector: 'app-user-session-attendance',
  standalone: true,
  imports: [
    CommonModule, FormsModule,
    SelectButtonModule,
    UserNameOrEmailPipe, LocalizedDatePipe, SessionAttendanceStatusBarComponent, ReservationStatusComponent,
    NgbTooltipModule, TranslateModule
  
  ],
  templateUrl: './user-session-attendance.component.html',
  styleUrls: ['./user-session-attendance.component.scss']
})
export class UserSessionAttendanceComponent implements OnInit, OnDestroy {
  SessionAttendanceState = SessionAttendanceState;
  ReservationState = ReservationState;
  SessionGroupCoupleRole = SessionGroupCoupleRole;

  @Input() ua: IUserSessionAttendanceFE | undefined;
  @Input() sessionAttendance: ISessionAttendanceFE | undefined;
  @Input() FG: FormGroup<IUserSessionAttendanceFG> | undefined;

  presentOption: ISelectButtonColoredItem = { value: SessionAttendanceState.PRESENT, icon: 'pi pi-check', color: Color.SUCCESS };
  replacedOption: ISelectButtonColoredItem = { value: -1, icon: 'pi pi-check', color: '--primary-color' };
  replacedSecondOption: ISelectButtonColoredItem = { value: SessionAttendanceState.ABSENT, icon: 'pi pi-times', color: '--primary-color' };
  absentExcusedOption: ISelectButtonColoredItem = { value: SessionAttendanceState.ABSENT, icon: 'pi pi-times', color: Color.WARN };
  absentNotExcusedOption: ISelectButtonColoredItem = { value: SessionAttendanceState.ABSENT, icon: 'pi pi-times', color: Color.DANGER };
  
  options: ISelectButtonColoredItem[] = [
    this.presentOption,
    this.absentExcusedOption,
  ];

  stateModel: SessionAttendanceState | null = null;
  excused: boolean = false;
  replaced: boolean = false;

  btnUniqueClass: string = this.makeid();

  subs: Subscription[] = [];

  constructor(
    private userStore: UserStoreService,
    public selectedOrg: SelectedOrgService,
    private modalService: ModalService,
  ) {}

  ngOnInit(): void {
    this.setStateModel();
    this.FG?.valueChanges.subscribe((v) => {
      this.setStateModel();
    })
  }

  private setStateModel() {
    this.stateModel = this.FG?.controls.state.value ?? null;
    this.replaced = this.FG?.controls.replaced.value ?? false;
    this.excused = this.FG?.controls.excused.value ?? false;

    this.setOptions();
    this.handleOptionColors();
  }

  private setOptions() {
    if (this.replaced) {
      const options = [
        this.replacedOption,
        this.replacedSecondOption
      ];
      this.options = options;
    } else {
      const options = [ this.presentOption ];
  
      if (this.excused) options.push( this.absentExcusedOption );
      else options.push( this.absentNotExcusedOption );
  
      this.options = options;
    }
  }

  ngAfterViewInit() {
  }

  onUserClick(user: IUser) {
    const orgId = this.selectedOrg.getCurrentValue();
    if (!orgId) return;
    if (this.userStore.hasAnyUserRole([ RoleType.SYSTEM_ADMIN, RoleType.ORG_ADMIN, RoleType.ORG_OWNER, RoleType.COORDINATOR ], orgId)) {
      this.modalService.openAdminCustomerCardModal({
        c: AdminCustomerCardModalComponent,
        userId: user.id,
        activeTab: AdminCustomerCardTab.ATTENDANCE
      })
    } else if (this.userStore.hasUserRole(RoleType.LECTURER, orgId)) {
      this.modalService.openLecturerCustomerCardModal({
        c: LecturerCustomerCardModalComponent,
        userId: user.id,
        activeTab: LecturerCustomerCardTab.ATTENDANCE
      });
    }
  }

  onOptionClick(e: SelectButtonOptionClickEvent) {
    if (this.FG?.disabled) {
      console.error('disabled')
      return;
    }
    const val = e.option.value as SessionAttendanceState;
    let stateValToSet = null;
    let excuseValToSet = false;
    const currentState = this.FG?.controls.state.value;
    const currentExcused = this.FG?.controls.excused.value;
    const currentReplaced = this.FG?.controls.replaced.value;

    // dont update if is replaced
    if (currentReplaced) {
      return;
    };


    if (currentState === val && currentExcused === false) {
      stateValToSet = null;
    }
    else {
      stateValToSet = val;
    }

    if (stateValToSet === SessionAttendanceState.PRESENT) {
      excuseValToSet = false;
    } else if (stateValToSet === SessionAttendanceState.ABSENT) {
      if (currentExcused === false) {
        excuseValToSet = true;
      } else if (currentExcused === true) {
        excuseValToSet = false;
      } else {
        excuseValToSet = false;
        stateValToSet = null;
      }
    }


    this.FG?.patchValue({
      excused: excuseValToSet,
      state: stateValToSet,
    });

    // update current state so it will be reflected in the UI
    const item = this.ua?.sessionAttendances.find(x => x.session.id === this.sessionAttendance?.session?.id) as unknown as ISessionAttendance;
    if (item) {
      item.excused = excuseValToSet;
      item.state = stateValToSet;
    }
  }

  handleOptionColors() {
    setTimeout(() => {
      (document.querySelectorAll(`.${this.btnUniqueClass} .p-selectbutton > .p-button`) as unknown as NodeListOf<HTMLElement>).forEach((opt, i) => {
        const color = this.options[i].color;
        if (!color) return;
        if (!opt.classList.contains('p-highlight') && this.stateModel === this.options[i].value) { // primeng fix
          opt.classList.add('p-highlight');
        }
        if (opt.classList.contains('p-highlight')) {
          opt.style.background = `var(${color})`;
          opt.style.borderColor = `var(${color})`;
        } else {
          opt.style.removeProperty('background');
          opt.style.removeProperty('border-color');
        }
      });
    })
  }


  makeid() {
    var result           = '';
    var characters       = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    var charactersLength = characters.length;
    for ( var i = 0; i < 10; i++ ) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
    }
    return result;
  }

  ngOnDestroy(): void {
    this.subs.forEach(x => x.unsubscribe());
  }
}
